// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Data
import DataTable from "../../components/table";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, MenuItem, Modal, Typography,
} from "@mui/material";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import Api from "../../api/api";
import { HashLoader } from "react-spinners";
import { format } from "date-fns";
import {
  Link, useNavigate, useParams, useSearchParams,
} from "react-router-dom";
import MDButton from "../../components/MDButton";
import { useIsFirstRender } from "usehooks-ts";
import workWithResponse from "../../functions/workWithResponse";
import { PagesVacancyStyles } from "./PagesVacancyStyles";

const modalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  display: "grid",
  gap: "24px",
};

const RowMenu = ({ id, handleDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem onClick={handleClose}>
          <Link to={`/vacancy/edit/${id}`} style={{ color: "#7b809a", width: "100%", height: "100%" }}>
            Edit
          </Link>
        </MenuItem>
        <MenuItem onClick={handleClose}>
          <Link to="/resumes/1" state={{ vacancyID: id }} style={{ color: "#7b809a", width: "100%", height: "100%" }}>
            Resumes
          </Link>
        </MenuItem>
        <MenuItem onClick={() => {
          handleDelete(id);
          handleClose();
        }}
        >
          Delete
        </MenuItem>
      </Menu>
    </div>
  );
};

function PagesVacancyTable() {
  const { pageNumber } = useParams();
  const navigate = useNavigate();
  const isFirst = useIsFirstRender();

  const [pagesVacancies, setPagesVacancies] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [vacanciesCount, setVacanciesCount] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [activeModalName, setActiveModalName] = useState(null);
  const [pageToDeleteId, setPageToDeleteId] = useState(null);
  const [deletingError, setDeletingError] = useState(true);

  const sortedAccessors = [];

  useEffect(() => {
    setActivePage(+pageNumber);
  }, []);

  async function getVacancies() {
    const { data: pagesVacancy, count } = await workWithResponse(() => Api.getVacancies((activePage - 1) * 10, 10)).then((res) => {
      if (!res.error && res.data) {
        return res.data;
      }
    });

    if (count !== vacanciesCount) {
      setVacanciesCount(count);
    }

    setPagesVacancies(pagesVacancy);
  }

  useEffect(() => {
    if (!isFirst) {
      setIsLoading(true);
      if (activePage !== pageNumber) {
        navigate(`/vacancy/${activePage}`);
      }

      getVacancies().then(() => setIsLoading(false));
    }
  }, [activePage]);

  useEffect(() => {
    async function getInitialData() {
      const pagesVacancies = await workWithResponse(() => Api.getVacancies()).then((res) => {
        if (!res.error && res.data) {
          setVacanciesCount(res.data.count);

          return res.data.data;
        }
      });

      setPagesVacancies(pagesVacancies);
    }

    getInitialData().then(() => setIsLoading(false));
  }, []);

  const columns = useMemo(() => (
    [
      {
        Header: "Name", accessor: "name", width: "30%", align: "left",
      },
      { Header: "Country", accessor: "country", align: "left" },
      { Header: "IsActive", accessor: "isActive", align: "left" },
      { Header: "action", accessor: "action", align: "center" },
    ]
  ), []);

  function handleDeletingVacancy(id) {
    if (deletingError) {
      setDeletingError(false);
    }

    setActiveModalName("deleting");
    setPageToDeleteId(id);
  }

  const rows = useMemo(() => {
    if (pagesVacancies) {
      return (
        pagesVacancies.map((page) => (
          {
            name: (
              <MDTypography component="span" variant="button" color="text" fontWeight="medium">
                {page.name}
              </MDTypography>
            ),
            country: (
              <MDTypography component="span" href="#" variant="button" color="text" fontWeight="medium">
                {page.country}
              </MDTypography>
            ),
            isActive: (
              <MDTypography component="span" href="#" variant="button" color="text" fontWeight="medium">
                {page.isActive ? "active" : "disable"}
              </MDTypography>
            ),
            action: (
              <RowMenu id={page.id} handleDelete={handleDeletingVacancy} />
            ),
          }
        ))
      );
    }
  }, [pagesVacancies]);

  return (
    <PagesVacancyStyles>
      <DashboardLayout>
        {activeModalName === "deleting" && (
          <Modal
            open={activeModalName === "deleting"}
            onClose={() => setActiveModalName(null)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={modalStyles}>
              <Typography id="modal-modal-title" variant="h6" component="h2">
                Confirm deleting
                {" "}
                {pagesVacancies.find((page) => page.id === pageToDeleteId).name}
                {" "}
                page.
              </Typography>

              <Box display="grid" sx={{ gap: "10px" }}>
                <Grid gridColumn={1}>
                  <MDButton fullWidth onClick={() => setActiveModalName(null)} variant="contained" color="info">
                    Cancel
                  </MDButton>
                </Grid>
                <Grid gridColumn={2}>
                  <MDButton
                    fullWidth
                    variant="contained"
                    color="error"
                    onClick={() => {
                      workWithResponse(() => Api.deleteVacancy(pageToDeleteId)).then((res) => {
                        if (res.data && !res.error) {
                          getVacancies();
                          setActiveModalName(null);
                        }
                      });
                    }}
                  >
                    Delete
                  </MDButton>
                </Grid>
              </Box>

              {deletingError && (
                <MDTypography component="span" href="#" variant="caption" color="error" fontWeight="medium">
                  *An error occurred while deleting
                </MDTypography>
              )}
            </Box>
          </Modal>
        )}

        <DashboardNavbar propsRoute={[""]} propsTitle="Pages vacancies" />
        <div className="content">
          {!isLoading && pagesVacancies ? (
            <div className="tableWrapper">

              <Grid container spacing={6}>
                <Grid item xs={12}>
                  <Card>
                    <MDBox
                      mx={2}
                      mt={-3}
                      py={3}
                      px={2}
                      variant="gradient"
                      bgColor="info"
                      borderRadius="lg"
                      coloredShadow="info"
                    >
                      <div className="tableHeader">
                        <MDTypography variant="h6" color="white">
                          Pages vacancies
                        </MDTypography>

                        <div className="tableHeaderButtonsWrapper">

                          <Link to="/vacancy/create">
                            <Button variant="contained" color="info">
                              <MDTypography variant="button" color="info" fontWeight="bold">
                                Add new
                              </MDTypography>
                            </Button>
                          </Link>
                        </div>
                      </div>

                    </MDBox>

                    <MDBox pt={3}>
                      {vacanciesCount > 0 ? (
                        <DataTable
                          table={{ columns, rows }}
                          isSorted={false}
                          entriesPerPage
                          showTotalEntries
                          noEndBorder
                          sortedAccessors={sortedAccessors}
                          pagination
                          activePage={activePage}
                          pagesCount={vacanciesCount}
                          setActivePage={setActivePage}
                          countOnPage={10}
                        />
                      ) : (
                        <MDBox variant="" fontWeight="regular" color="error" sx={{ textAlign: "center", fontSize: "18px", marginBottom: "12px" }}>
                          No pages vacancies were found for your request
                        </MDBox>
                      )}
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            </div>
          ) : (
            <div className="loaderWrapper">
              <HashLoader color="#49a3f1" />
            </div>
          )}
        </div>
      </DashboardLayout>
    </PagesVacancyStyles>
  );
}

export default PagesVacancyTable;
